import type {EmptyStateProps} from 'shells/card/card-widget/card-widget';
import type {TabProps} from 'shells/control-bar/page-control-bar';
import {BusinessCardRegularIcon} from 'shells/icon/svg/business-card-regular-icon';
import type {SelectMenuOption} from 'shells/select-menu/select-menu';

import type {PeepBusinessCardInfoFragment} from 'nutshell-graphql-types';

import type {PatchTitleInput} from './types';

function getAllBusinessCardIds(
    peepBusinessCardInfo: PeepBusinessCardInfoFragment | null | undefined
): Set<string> {
    const emails = peepBusinessCardInfo?.emailAddresses.all || [];
    const phones = peepBusinessCardInfo?.phones.all || [];
    const postalAddresses = peepBusinessCardInfo?.postalAddresses.all || [];
    const urls = peepBusinessCardInfo?.urls.all || [];

    const idsArray = [...emails, ...phones, ...postalAddresses, ...urls].map((value) => value.id);

    return new Set(idsArray);
}

export function getNewlyAddedId(
    before: PeepBusinessCardInfoFragment | null | undefined,
    after: PeepBusinessCardInfoFragment | null | undefined
): string | null {
    const beforeIds = getAllBusinessCardIds(before);
    const afterIds = getAllBusinessCardIds(after);

    return Array.from(afterIds).find((id) => !beforeIds.has(id)) || null;
}

export function getEmptyStateProps(
    peepBusinessCardInfo: PeepBusinessCardInfoFragment | null | undefined
): EmptyStateProps {
    return {
        isEmpty: peepBusinessCardInfo
            ? peepBusinessCardInfo.emailAddresses.all.length === 0 &&
              peepBusinessCardInfo.phones.all.length === 0 &&
              peepBusinessCardInfo.postalAddresses.all.length === 0 &&
              peepBusinessCardInfo.urls.all.length === 0
            : true,
        text: 'No contact details',
        icon: BusinessCardRegularIcon,
        iconSize: 20,
    };
}

export function getTabProps(
    hasFormerItems: boolean,
    selectedTab: 'current' | 'former',
    setSelectedTab: (tab: 'current' | 'former') => void
): TabProps | undefined {
    if (!hasFormerItems) {
        setSelectedTab('current');
        return undefined;
    }

    return {
        options: [
            {
                id: 'current',
                label: 'Current',
            },
            {
                id: 'former',
                label: 'Former',
            },
        ],
        selectedTab,
        onChangeTab: (newTab: string) => setSelectedTab(newTab as 'current' | 'former'),
    };
}

// Takes in an array of any type of items that have an isFormer property and
// returns an object with two arrays: current and former.
export function splitByFormerStatus<T extends {isFormer?: boolean | null}>(
    items: T[] | null | undefined
): {current: T[]; former: T[]} {
    const current: T[] = [];
    const former: T[] = [];

    for (const item of items ?? []) {
        if (item.isFormer) {
            former.push(item);
        } else {
            current.push(item);
        }
    }

    return {current, former};
}

export function sortByPrimaryStatus<T extends {isPrimary?: boolean | null}>(
    items: T[] | null | undefined
): T[] {
    return [...(items ?? [])].sort((a, b) => (a.isPrimary ? -1 : 0) - (b.isPrimary ? -1 : 0));
}

export function getTitleDropdownOptions(
    labelOptions: string[] | null | undefined,
    attributeValueId: string,
    onUpdateTitle: (args: PatchTitleInput) => Promise<any>
): SelectMenuOption[] | undefined {
    return labelOptions?.map((label) => {
        const option: SelectMenuOption = {
            label,
            handleSelect: (selectedOption: SelectMenuOption) => {
                return onUpdateTitle({
                    attributeValueId,
                    name: typeof selectedOption.label === 'string' ? selectedOption.label : label,
                });
            },
        };

        return option;
    });
}
